import React, { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import styles from "./scoreCardPage.module.scss";
import pl from "shared/messages/pl";
import { Loader } from "ecom-teams-apps-core";
import { ScorecardReportV3Filter_PublicLevel, ScorecardV3_Public } from "shared/service/api/generated";
import { useQuery } from "react-query";
import EmptyContent from "view360Card/components/emptyContent/EmptyContent";
import ScoresService from "goalsCard/service/ScoresService";
import CompanyScoreReportSection from "./companyScoreReportSection/CompanyScoreReportSection";
import ScoreReportsTable from "./scoreReportsTable/ScoreReportsTable";
import CompanyGoalsSummaryReportSection from "./companyScoreReportSection/CompanyGoalsSummaryReportSection";
import ReportPage from "shared/reports/components/reportPage/ReportPage";
import { IMineTenantElements, IReportEntity, ITenantTableColumn } from "shared/reports/models/models";
import { ScorecardGoal, ScorecardReportEntity } from "goalsCard/service/api/models";
import { formatDate } from "utils/formattingUtils";
import { constants } from "shared/constants";
import { toFixedNumber } from "utils/numberUtils";
import { IReportEntityType } from "shared/constants/reportConstants";

export interface ScoreCardPageProps {
    level?: ScorecardReportV3Filter_PublicLevel;
    scoreCardPeriod: ScorecardV3_Public;
}

const ScoreCardPage: React.FC<ScoreCardPageProps> = observer((props: ScoreCardPageProps) => {
    const { isFetching, data } = useQuery(["scorecard-page", props.level, props.scoreCardPeriod], async () => {
        return await ScoresService.getScoreCardReport(props.level, props.scoreCardPeriod.token);
    });

    const renderLoader = () => {
        return (
            <div className={styles.loaderWrapper}>
                <Loader text={pl.reports.loading} />
            </div>
        );
    };

    const renderSummarySection = () => {
        if (data === undefined) {
            return null;
        }

        const showEntityTypeInTitle = data.entitiesByType.length > 1;

        return (
            <>
                {data.entityGoalsSummaryList
                    .filter((i) => i.hasGoalsOtherThanMine)
                    .map((entityGoalsSummary) => {
                        return entityGoalsSummary.tenantGoalsSummaryList.map((tenantGoalsSummary) => {
                            return (
                                <CompanyGoalsSummaryReportSection
                                    key={`CompanyScopreReportSection_goalssummary_${entityGoalsSummary.entityType.code}_${tenantGoalsSummary.tenant.code}`}
                                    todayProgressValue={props.scoreCardPeriod.timeProgress}
                                    additionalClassName={styles.userTargetReportWrapperItem}
                                    companyName={`${pl.reports.summaryTitle(
                                        entityGoalsSummary.entityType.displayName,
                                        tenantGoalsSummary.tenant.displayName,
                                        showEntityTypeInTitle
                                    )}`}
                                    goals={tenantGoalsSummary.goalSummary}
                                />
                            );
                        });
                    })}
            </>
        );
    };

    const renderTableSection = (
        entityType: IReportEntityType,
        entitiesForTable: IReportEntity[],
        tenantTableColumns: ITenantTableColumn[]
    ): ReactNode => {
        return (
            <ScoreReportsTable
                key={`ScoreReportsTable_${entityType.code}`}
                reports={entitiesForTable as ScorecardReportEntity[]}
                tenantTableColumns={tenantTableColumns}
            />
        );
    };

    const renderCompanySectionElements = (mineTenantElements: IMineTenantElements) => {
        return (
            <CompanyScoreReportSection
                key={`CompanyScopreReportSection_${mineTenantElements.tenant.code}`}
                todayProgressValue={props.scoreCardPeriod.timeProgress}
                additionalClassName={styles.userTargetReportWrapperItem}
                companyName={mineTenantElements.tenant.displayName}
                goals={mineTenantElements.elements as ScorecardGoal[]}
            />
        );
    };

    if (isFetching) {
        return renderLoader();
    }

    if (data?.list === undefined || data?.list.length === 0) {
        return <EmptyContent message={pl.common.noData} graphic="welcome" />;
    }

    return (
        <ReportPage
            report={data}
            subtitleRow1={pl.reports.headers.resultsRange(
                formatDate(props.scoreCardPeriod.startDate),
                formatDate(props.scoreCardPeriod.endDate)
            )}
            subtitleRow2={pl.reports.headers.dataProgress(
                props.scoreCardPeriod.timeProgress === undefined
                    ? constants.noValue
                    : toFixedNumber(Math.min(props.scoreCardPeriod.timeProgress!, 100), 1).toLocaleString("pl")
            )}
            renderCompanySectionElements={renderCompanySectionElements}
            renderSummarySection={renderSummarySection}
            renderTable={renderTableSection}
        />
    );
});

export default ScoreCardPage;
